import { Box, Chip, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, MenuItem, OutlinedInput, Radio, RadioGroup, Rating, Select, Typography } from '@mui/material';
import { FormControlLabelStyled, FormControlLabeNPSlStyled, RadioGroupNPSStyled } from './style';
import { useEffect, useRef, useState } from 'react';
import { Parser } from 'expr-eval';
import CustomTooltip from '../../components/CustomTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// For date
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import utc from 'dayjs/plugin/utc';
import { DatePicker } from '@mui/x-date-pickers';
import RenderContent from '../../components/RenderContent';
import { DocumentUpload } from '../../components/DocumentUpload';
import { prettifyWord } from '../../utils';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Picklist
export const SelectInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled, fullWidth }) => {
    const [selectedValue, setSelectedValue] = useState(null);

    const updateValues = (value, isDefaultValue) => {
        setSelectedValue(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleChange = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const dv = question.Default_Value__c;
            const options = question?.Options__r?.records || [];
            const option = options && options?.length > 0 ? options.find((c) => c.Label__c === dv) : null;

            if (option?.Label__c) {
                updateValues(dv, true);
            }
        } else {
            setSelectedValue(question.answer);
        }
    }, []);

    const options = question?.Options__r?.records?.sort((a, b) => a.Order__c - b.Order__c) || [];
    const htmlForId = screenNo + question.Id;
    return (
        <FormControl sx={{ width: '100%' }}>
            <ShowLabel question={question} questionNumber={questionNumber} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <Select
                name="select"
                displayEmpty
                sx={(theme) => ({
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    border: `1px solid rgba(250, 250, 250, 0)`,

                    '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        border: `1px solid rgba(228,228,228, 0)`,
                    },
                })}
                error={false}
                value={selectedValue}
                onChange={handleChange}
                disabled={disabled}
                input={
                    <OutlinedInput
                        id={htmlForId}
                        inputProps={{
                            sx: {
                                padding: '10px 16px',
                                border: `1px solid rgba(228,228,228, 0)`,
                                borderRadius: '4px',
                            },
                        }}
                    />
                }
                renderValue={(selected) => {
                    if (!selected) {
                        return (
                            <Box component={'span'} sx={{ color: '#b6b6b6' }}>
                                Select an answer
                            </Box>
                        );
                    }

                    const option = options && options?.length > 0 ? options.find((c) => c.Label__c === selected) : null;
                    return option?.Label__c;
                }}
                inputProps={{ 'aria-label': 'Select label' }}
            >
                <MenuItem disabled value="">
                    <em>Select an answer</em>
                </MenuItem>

                {options &&
                    Array.isArray(options) &&
                    options.map((cOption) => {
                        return (
                            <MenuItem key={cOption.Id} value={cOption.Label__c}>
                                {cOption.Label__c}
                            </MenuItem>
                        );
                    })}
            </Select>
            <FormHelperText error>{question.error}</FormHelperText>
        </FormControl>
    );
};

export const RadioInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [selectedvalue, setSelectedValue] = useState('');

    const updateValues = (value, isDefaultValue) => {
        setSelectedValue(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleSelect = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const dv = question.Default_Value__c;
            const options = question?.Options__r?.records || [];
            const option = options && options?.length > 0 ? options.find((c) => c.Label__c === dv) : null;
            if (option?.Label__c) {
                updateValues(dv, true);
            }
        } else {
            setSelectedValue(question.answer);
        }
    }, []);

    const options = question?.Options__r?.records?.sort((a, b) => a.Order__c - b.Order__c) || [];
    const htmlForId = screenNo + question.Id;
    return (
        <Box component={'fieldset'} sx={{ border: 'unset' }}>
            <ShowLabel question={question} questionNumber={questionNumber} component={'legend'} id="radio-buttons" htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <RadioGroup sx={{ maxWidth: '700px' }} aria-labelledby="radio-buttons" name="radio-buttons-group" value={selectedvalue || question.answer} onChange={handleSelect} disabled={disabled}>
                {options &&
                    Array.isArray(options) &&
                    options.map((cOption) => {
                        return (
                            <FormControlLabelStyled
                                key={cOption.Id}
                                className={`${selectedvalue === cOption.Label__c ? 'selected' : ''}`}
                                control={<Radio disabled={disabled} />}
                                // value={cOption.Id}
                                value={cOption.Label__c}
                                label={cOption.Label__c}
                            />
                        );
                    })}
            </RadioGroup>
            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const TextInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [text, setText] = useState('');

    const updateValues = (value, isDefaultValue) => {
        setText(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleChange = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            updateValues(question.Default_Value__c, true);
        } else {
            setText(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box>
            {/* Input text */}
            <ShowLabel question={question} questionNumber={questionNumber} component={InputLabel} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <OutlinedInput
                id={htmlForId}
                name={'name'}
                placeholder="Type your answer here..."
                inputProps={{
                    sx: {
                        padding: '10px 16px',
                    },
                }}
                disabled={disabled}
                // error={true}
                value={text}
                onChange={handleChange}
                sx={(theme) => ({
                    width: {
                        xs: '100%',
                        md: '100%',
                    },
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.border.neutralPrimary}`,
                    },
                })}
            />
            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const TextareaInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [text, setText] = useState('');

    const updateValues = (value, isDefaultValue) => {
        setText(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleChange = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            updateValues(question.Default_Value__c, true);
        } else {
            setText(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box>
            {/* Input text */}
            <ShowLabel question={question} questionNumber={questionNumber} component={InputLabel} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <OutlinedInput
                id={htmlForId}
                name="name"
                placeholder="Type your answer here..."
                inputProps={{
                    sx: {
                        padding: '10px 16px',
                    },
                }}
                disabled={disabled}
                value={text}
                onChange={handleChange}
                multiline // 👈 makes it multiline
                rows={3} // 👈 sets ~3 lines height
                sx={(theme) => ({
                    width: {
                        xs: '100%',
                        md: '100%',
                    },
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    '& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
                        padding: 0,
                    },
                    '&.MuiInputBase-root.MuiOutlinedInput-root': {
                        padding: '12px 16px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.border.neutralPrimary}`,
                    },
                })}
            />

            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const StarsRatingInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [selectedStars, setSelectedStars] = useState(null);

    const handleSelectedStarsChange = (event, newValue, isDefaultValue) => {
        setSelectedStars(newValue);
        handleDataChange(newValue, question.Id, screenNo, null, isDefaultValue);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const num = Number(question.Default_Value__c);
            let ansNum = Number.isFinite(num) ? num : null;
            ansNum = ansNum < 1 || ansNum > 5 ? null : ansNum;
            if (ansNum) {
                handleSelectedStarsChange(null, ansNum, true);
            }
        } else {
            setSelectedStars(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box>
            <ShowLabel question={question} questionNumber={questionNumber} component={'legend'} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <Rating
                id={htmlForId}
                name="simple-controlled"
                size="large"
                value={selectedStars}
                max={5}
                precision={0.5}
                disabled={disabled}
                onChange={handleSelectedStarsChange}
                sx={{
                    fontSize: '2.5rem',
                    '& .MuiRating-icon.MuiRating-iconEmpty': {
                        color: '#71809666',
                    },
                    maxWidth: '700px',
                }}
            />
            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const NPSRatingInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled, isCardView = false }) => {
    const [recommendationScore, setRecommendationScore] = useState(null);

    const updateValues = (value, isDefaultValue) => {
        setRecommendationScore(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleRecommendationScoreChange = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const num = Number(question.Default_Value__c);
            let ansNum = Number.isFinite(num) ? num : null;
            ansNum = ansNum < 0 || ansNum > 10 ? null : ansNum;
            if (ansNum !== null) {
                updateValues(`${ansNum}`, true);
            }
        } else {
            setRecommendationScore(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box component={'fieldset'} sx={{ border: 'unset' }}>
            <ShowLabel question={question} questionNumber={questionNumber} component={'legend'} id="radio-buttons" htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <Box width={'fit-content'} maxWidth={'700px'}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" fontWeight="400" color={'#0B0B0B'} fontSize={'12px'}>
                        Not at all likely
                    </Typography>
                    <Typography variant="caption" fontWeight="400" color={'#0B0B0B'} fontSize={'12px'}>
                        Extremely likely
                    </Typography>
                </Box>
                <RadioGroupNPSStyled aria-labelledby="radio-buttons" name="radio-buttons-group" value={recommendationScore} onChange={handleRecommendationScoreChange} isCardView={isCardView}>
                    {Array.from({ length: 11 }).map((_, index) => (
                        <FormControlLabeNPSlStyled key={index} disabled={disabled} control={<Radio />} value={index} label={`${index}`} isCardView={isCardView} />
                    ))}
                </RadioGroupNPSStyled>
            </Box>

            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const DateInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [value, setValue] = useState(null);
    const [error, setError] = useState(null);
    const [touched, setTouched] = useState(false);

    const handleChange = (newValue, isDefaultValue) => {
        // handle change fire when newValue is not null
        const dateString = newValue?.format('MM/DD/YYYY');
        setValue(dateString);
        handleDataChange(dateString, question.Id, screenNo, null, isDefaultValue);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            // we can verify Default_Value__c is MM/DD/YYYY here first
            const dv = question.Default_Value__c;
            const isValidDate = dayjs(dv, 'YYYY-MM-DD HH:mm:ss', true).isValid() || dayjs(dv, 'MM/DD/YYYY', true).isValid();
            const displayValue = typeof dv === 'string' && isValidDate ? dayjs(dv) : null;
            // const displayValue = typeof dv === 'string' && dayjs(dv, 'YYYY-MM-DD HH:mm:ss', true).isValid() ? dayjs(dv, 'YYYY-MM-DD HH:mm:ss') : null;
            if (displayValue) {
                handleChange(displayValue, true);
            }
        }
    }, []);

    const displayValue = dayjs(value ? value : question.answer ? question.answer : null);
    const htmlForId = screenNo + question.Id;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <DemoContainer
                    components={['DatePicker']}
                    sx={{
                        width: '100%',
                        margin: 0,
                        padding: 0,
                        display: 'flex',
                        flexDirection: { lg: 'column' },
                        '&>:not(style):not(style)': {
                            margin: 0,
                            marginBottom: '0.4rem',
                            marginTop: { xs: '0' },
                        },
                    }}
                >
                    <ShowLabel question={question} questionNumber={questionNumber} component={'label'} htmlFor={htmlForId} />
                    <ShowDescription
                        sx={{
                            marginBottom: '0.4rem !important',
                            marginLeft: { xs: '4px !important' },
                        }}
                        description={question.Description__c}
                    />
                    <DatePicker
                        clearable
                        fullWidth
                        onError={(error, value) => {
                            // console.log('date error: ', error, value);
                            setError(typeof error === 'string' ? prettifyWord(error) : error);
                        }}
                        error={touched && Boolean(error)} // only show after user interaction
                        value={displayValue}
                        onChange={handleChange}
                        disabled={disabled}
                        sx={(theme) => ({
                            '& .MuiInputBase-root.MuiOutlinedInput-root': {
                                width: '100%',
                                maxWidth: '700px',
                                borderRadius: '4px',
                                background: theme.palette.background.default,

                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: `1px solid ${theme.palette.border?.neutralPrimary || '#ccc'}`,
                                },
                                '&.Mui-focused': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: `2px solid ${theme.palette.action.active || '#ccc'}`,
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    padding: '10px 16px',
                                },
                            },
                            '&.MuiFormControl-root.MuiTextField-root': {
                                marginLeft: 0,
                            },
                        })}
                        slotProps={{
                            textField: {
                                onBlur: () => setTouched(true),
                                id: htmlForId,
                            },
                        }}
                    />
                </DemoContainer>
                <FormHelperText error>{(touched && error) || question.error}</FormHelperText>
                {/* <FormHelperText error>{question.error}</FormHelperText> */}
            </Box>
        </LocalizationProvider>
    );
};

export const UploadInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled, parentId, displayItem, displayItemSizes = {} }) => {
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [fileCount, setFileCount] = useState(0);
    const [displayFiles, setDisplayFiles] = useState(true);
    const uploadDocumentRef = useRef();
    const uploadDocumentReadonlyRef = useRef();

    useEffect(() => {
        handleDataChange(fileCount == 0 ? '' : fileCount, question.Id, screenNo, true, true);
    }, [fileCount]);

    const handleChange = (event) => {
        const value = event.target.value;

        setValue(value);
        handleDataChange(value, question.Id, screenNo);
    };

    const handleFileUploadComplete = () => {
        uploadDocumentReadonlyRef.current.refreshFiles();
    };

    const htmlForId = screenNo + question.Id;

    return (
        <Box>
            {/* Input text */}
            <ShowLabel question={question} questionNumber={questionNumber} component={InputLabel} htmlFor={htmlForId} noHtmlFor={true} />
            <ShowDescription description={question.Description__c} />
            <DocumentUpload ref={uploadDocumentRef} parentId={parentId} displayItem={displayItem} userDetails={{}} onFileUploadComplete={handleFileUploadComplete} disabled={disabled}></DocumentUpload>
            <DocumentUpload
                ref={uploadDocumentReadonlyRef}
                parentId={parentId}
                readonly
                setLoading={setLoading}
                displayItem={displayItem}
                getCount
                onFileUploadComplete={handleFileUploadComplete}
                disabled={disabled}
                setFileCount={setFileCount}
                absoluteMenuIcon={true}
                displayItemSizes={displayItemSizes}
            ></DocumentUpload>
            <FormHelperText sx={{ marginTop: '10px' }} error>
                {question.error}
            </FormHelperText>
        </Box>
    );
};

export const NumberInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [value, setValue] = useState('');

    const updateValues = (value, isDefaultValue) => {
        setValue(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    };

    const handleChange = (event) => {
        const value = event.target.value;
        updateValues(value);
    };

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const isValid = isValidNumberString(question.Default_Value__c);
            if (isValid) {
                updateValues(question.Default_Value__c, true);
            }
        } else {
            setValue(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box>
            {/* Input text */}
            <ShowLabel question={question} questionNumber={questionNumber} component={InputLabel} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <OutlinedInput
                id={htmlForId}
                name={'name'}
                type="number"
                placeholder="Type number..."
                inputProps={{
                    sx: {
                        padding: '10px 16px',
                    },
                }}
                error={false}
                value={value}
                disabled={disabled}
                onChange={handleChange}
                sx={(theme) => ({
                    width: {
                        xs: '100%',
                        md: '100%',
                    },
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.border.neutralPrimary}`,
                    },
                })}
            />
            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const TitleText = ({ question }) => {
    return (
        <Box className="titleText">
            {/* Show Title Text */}
            <ShowDescription description={question.Description__c} />
        </Box>
    );
};

export const MultiSelectInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled, fullWidth }) => {
    const [selectedValues, setSelectedValues] = useState([]);

    const handleChange = (event) => {
        const value = event.target.value; // array
        updateValues(value);
    };

    function updateValues(value, isDefaultValue) {
        const arrayValue = typeof value === 'string' ? value.split(',') : value;
        setSelectedValues(arrayValue);
        const stringValue = typeof value === 'string' ? value : value?.join(',');
        handleDataChange(stringValue, question.Id, screenNo, null, isDefaultValue);
    }

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const value = question.Default_Value__c;
            const arrayValue = typeof value === 'string' ? value.split(',') : [];

            const options = question?.Options__r?.records || [];
            const foundOptions = [];
            arrayValue?.forEach((av) => {
                const option = options.find((c) => c.Label__c === av);
                if (option?.Label__c) {
                    foundOptions.push(option?.Label__c);
                }
            });
            const defaultOptionsString = foundOptions.join(',');

            if (defaultOptionsString) {
                updateValues(defaultOptionsString, true);
            }
        } else {
            const answer = question.answer;
            const answerList = answer ? (typeof answer === 'string' ? answer.split(',') : answer) : [];
            setSelectedValues(answerList);
        }
    }, []);

    const options = question?.Options__r?.records?.sort((a, b) => a.Order__c - b.Order__c) || [];
    const htmlForId = screenNo + question.Id;

    return (
        <FormControl sx={{ width: '100%' }}>
            <ShowLabel question={question} questionNumber={questionNumber} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <Select
                name="select"
                displayEmpty
                multiple
                sx={(theme) => ({
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    border: `1px solid rgba(250, 250, 250, 0)`,

                    '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        border: `1px solid rgba(228,228,228, 0)`,
                    },
                })}
                error={false}
                value={selectedValues}
                onChange={handleChange}
                disabled={disabled}
                input={
                    <OutlinedInput
                        id={htmlForId}
                        inputProps={{
                            sx: {
                                padding: '10px 16px',
                                border: `1px solid rgba(228,228,228, 0)`,
                                borderRadius: '4px',
                            },
                        }}
                    />
                }
                renderValue={(selected) => {
                    if (!selected || !selected?.length) {
                        return (
                            <Box component={'span'} sx={{ color: '#b6b6b6' }}>
                                Select an answer
                            </Box>
                        );
                    }
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                                <Chip
                                    key={value}
                                    label={value}
                                    sx={{
                                        color: '#000', // text color
                                    }}
                                />
                            ))}
                        </Box>
                    );
                }}
                inputProps={{ 'aria-label': 'Select label' }}
            >
                <MenuItem disabled value="">
                    <em>Select an answer</em>
                </MenuItem>

                {options &&
                    Array.isArray(options) &&
                    options.map((cOption) => {
                        return (
                            <MenuItem key={cOption.Id} value={cOption.Label__c}>
                                {cOption.Label__c}
                            </MenuItem>
                        );
                    })}
            </Select>
            <FormHelperText error>{question.error}</FormHelperText>
        </FormControl>
    );
};

export const CurrencyInput = ({ question, questionNumber = 1, handleDataChange = () => {}, screenNo, disabled }) => {
    const [currency, setCurrency] = useState('');

    const handleChange = (event) => {
        const value = event.target.value;
        // Allow: empty, digits, optional decimal with up to 2 digits
        const regex = /^\d*\.?\d{0,2}$/;

        if (value === '' || regex.test(value)) {
            updateValues(value);
        }
    };

    const handleBlur = () => {
        if (currency === '' || currency === '.') return;

        const num = Number(currency);
        if (!isNaN(num)) {
            // force 2 decimal places, e.g. 20 -> 20.00
            const value = num.toFixed(2);
            updateValues(value);
        }
    };

    function updateValues(value, isDefaultValue) {
        setCurrency(value);
        handleDataChange(value, question.Id, screenNo, null, isDefaultValue);
    }

    useEffect(() => {
        if (!question.answer && question.Default_Value__c !== null) {
            const dv = question.Default_Value__c;
            const isValid = isValidNumberString(dv);
            if (isValid) {
                const regex = /^\d*\.?\d{0,2}$/;
                (dv === '' || regex.test(dv)) && updateValues(dv, true);
            }
        } else {
            setCurrency(question.answer);
        }
    }, []);

    const htmlForId = screenNo + question.Id;
    return (
        <Box>
            {/* Input text */}
            <ShowLabel question={question} questionNumber={questionNumber} component={InputLabel} htmlFor={htmlForId} />
            <ShowDescription description={question.Description__c} />
            <OutlinedInput
                id={htmlForId}
                name={'currency'}
                type={'number'}
                placeholder="Type your answer here..."
                inputProps={{
                    sx: {
                        padding: '10px 16px',
                    },
                    inputMode: 'decimal',
                }}
                disabled={disabled}
                // error={true}
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                value={currency}
                onBlur={handleBlur}
                onChange={handleChange}
                sx={(theme) => ({
                    width: {
                        xs: '100%',
                        md: '100%',
                    },
                    maxWidth: '700px',
                    borderRadius: '4px',
                    background: theme.palette.background.default,
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.border.neutralPrimary}`,
                    },
                })}
            />
            <FormHelperText error>{question.error}</FormHelperText>
        </Box>
    );
};

export const ShowAdditionalInformation = ({ info = '' }) => {
    if (!info) {
        return null;
    }
    return (
        <CustomTooltip title={info}>
            <IconButton
                sx={(theme) => ({
                    marginLeft: '0.25rem',
                    color: 'gray.900',
                    padding: 0,

                    // '& svg': {
                    //     width: '18px',
                    //     height: '18px',
                    // },
                })}
            >
                <InfoOutlinedIcon fontSize="small" />
            </IconButton>
        </CustomTooltip>
    );
};
export const ShowDescription = ({ description = '', sx = {} }) => {
    if (!description) {
        return null;
    }
    return (
        <Box marginBottom={'0.5rem'} marginLeft={{ xs: '4px', sm: '0px' }} sx={{ ...sx }}>
            <RenderContent htmlString={description} />
        </Box>
    );
};
export const ShowLabel = ({ question = {}, questionNumber = '', component = 'label', fontWeight = 'fontWeightSemiBold', htmlFor = '', noHtmlFor = false, ...props }) => {
    return (
        <Box
            component={component}
            sx={(theme) => ({
                marginBottom: '0.4rem',
                display: 'flex',
                alignItems: 'flex-start',
                color: theme.palette.text.primary,
                whiteSpace: 'pre-line',
                position: 'relative',
                lineHeight: 'normal',
            })}
            fontWeight={fontWeight}
            {...(!noHtmlFor
                ? {
                      htmlFor: htmlFor ? htmlFor : question.Id,
                  }
                : {})}
            {...props}
        >
            {/* <Box
                component={'span'}
                display={'flex'}
                alignItems={'center'}
                color={'brandPrimary.500'}
                marginRight={'0.4rem'}
                sx={{
                    position: 'absolute',
                    top: '-2px',
                    left: '0',
                }}
            >
                {questionNumber} <ArrowRightAltIcon />
            </Box> */}
            <Box>
                {question.Label__c} {(question.Is_Required__c === 'Yes' || !question.Is_Required__c) && <span style={{ color: '#B00020' }}>*</span>}
            </Box>
            <ShowAdditionalInformation info={question.Additional_Information__c} />
        </Box>
    );
};

// ...Conditions evalution...

function isValidNumberString(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (typeof value === 'string') {
        return value.trim() !== '' && Number.isFinite(Number(value)); // not NaN, '', INFINITY
    }
    return false;
}

function isNumberStringsValid(num1, num2) {
    if (!isValidNumberString(num1) || !isValidNumberString(num2)) {
        console.error('Invalid number string: ', num1, num2);
        return false;
    }
    return true;
}

function evaluateExpression(expr, answers) {
    try {
        const parser = new Parser();
        const parsedExpr = parser.parse(expr);
        return parsedExpr.evaluate(answers);
    } catch (error) {
        console.warn('❌ Invalid expression:', expr, error.message);
        return false; // or false, depending on your use case
    }
}

export function evaluateCondition(rawCondition, answers) {
    // exprect like (1 AND 2) OR 3

    let expr = rawCondition.trim();
    expr = expr
        .replace(/\s+/g, ' ')
        .replace(/\bAND\b/gi, 'and')
        .replace(/\bOR\b/gi, 'or')
        .replace(/\bNOT\b/gi, 'not')
        .replace(/&&/g, 'and')
        .replace(/\|\|/g, 'or')
        .replace(/!/g, 'not ');

    // Step 3: Replace numbers with answer values (true/false)
    expr = expr.replace(/\b(\d+)\b/g, (_, num) => {
        // if (answers[num] === undefined) throw new Error(`Missing answer for Q${num}`);
        if (answers[num] === undefined) return false;
        return answers[num];
    });

    // Step 5: Evaluate safely
    // return Function(`"use strict"; return (${expr});`)();
    return evaluateExpression(expr, answers);
}

export function checkIsConditionMatched({ operator, answer, answerToMatch }) {
    if (operator === 'Equals To' && String(answer) === String(answerToMatch)) {
        return true;
    }
    if (operator === 'Greater Than' && isNumberStringsValid(answer, answerToMatch) && Number(answer) > Number(answerToMatch)) {
        return true;
    }
    if (operator === 'Greater Than or Equals' && isNumberStringsValid(answer, answerToMatch) && Number(answer) >= Number(answerToMatch)) {
        return true;
    }
    if (operator === 'Less Than' && isNumberStringsValid(answer, answerToMatch) && Number(answer) < Number(answerToMatch)) {
        return true;
    }
    if (operator === 'Less Than or Equals' && isNumberStringsValid(answer, answerToMatch) && Number(answer) <= Number(answerToMatch)) {
        return true;
    }
    if (operator === 'Not Equals To' && String(answer) !== String(answerToMatch)) {
        return true;
    }

    return false;
}

export function checkIsConditionMatchedForMultiSelect({ operator, answerArray, answerToMatch }) {
    if (operator === 'Equals To' && answerArray?.some((answer) => String(answer) === String(answerToMatch))) {
        return true;
    }
    if (operator === 'Not Equals To' && answerArray?.length > 0 && answerArray?.every((answer) => String(answer) !== String(answerToMatch))) {
        return true;
    }
    return false;
}

export function shouldQuestionOrScreenVisible({ conditionalRequirement, conditionalLogic, conditionList, questionList }) {
    if (!conditionalRequirement || conditionalRequirement === 'None') {
        return true;
    }
    if (conditionalRequirement.toUpperCase() === 'AND') {
        const getAllCondtions = conditionList || [];
        const booleanList = [];

        for (let index = 0; index < getAllCondtions.length; index++) {
            const condition = getAllCondtions[index];
            const sourceQuestion = questionList?.find((cQuestion) => cQuestion.Id === condition?.Source_Question__c);

            if (sourceQuestion && sourceQuestion?.Type__c === 'Multi-select') {
                let newAnswer = sourceQuestion?.answer || '';
                const answerArray = newAnswer.split(',') || [];

                const b = checkIsConditionMatchedForMultiSelect({
                    operator: condition?.Operator__c,
                    answerArray: answerArray,
                    answerToMatch: condition?.Value__c,
                });
                booleanList.push(b);
            } else {
                const b = checkIsConditionMatched({
                    operator: condition?.Operator__c,
                    answer: sourceQuestion?.answer,
                    answerToMatch: condition?.Value__c,
                });
                booleanList.push(b);
            }
        }

        let value = booleanList[0];
        for (let index = 1; index < booleanList.length; index++) {
            const b = booleanList[index];
            value = value && b;
        }

        return value;
    }
    if (conditionalRequirement.toUpperCase() === 'OR') {
        const getAllCondtions = conditionList || [];
        const booleanList = [];

        for (let index = 0; index < getAllCondtions.length; index++) {
            const condition = getAllCondtions[index];
            const sourceQuestion = questionList?.find((cQuestion) => cQuestion.Id === condition?.Source_Question__c);

            if (sourceQuestion && sourceQuestion?.Type__c === 'Multi-select') {
                let newAnswer = sourceQuestion?.answer || '';
                const answerArray = newAnswer.split(',') || [];

                const b = checkIsConditionMatchedForMultiSelect({
                    operator: condition?.Operator__c,
                    answerArray: answerArray,
                    answerToMatch: condition?.Value__c,
                });
                booleanList.push(b);
            } else {
                const b = checkIsConditionMatched({
                    operator: condition?.Operator__c,
                    answer: sourceQuestion?.answer,
                    answerToMatch: condition?.Value__c,
                });
                booleanList.push(b);
            }
        }

        let value = booleanList[0];
        for (let index = 1; index < booleanList.length; index++) {
            const b = booleanList[index];
            value = value || b;
        }

        return value;
    }
    if (conditionalRequirement.toUpperCase() === 'CUSTOM') {
        const getAllCondtions = conditionList || [];
        const booleanAnswerData = {};

        for (let index = 0; index < getAllCondtions.length; index++) {
            const condition = getAllCondtions[index];
            const sourceQuestion = questionList?.find((cQuestion) => cQuestion.Id === condition?.Source_Question__c);

            if (sourceQuestion && sourceQuestion?.Type__c === 'Multi-select') {
                let newAnswer = sourceQuestion?.answer || '';
                const answerArray = newAnswer.split(',') || [];
                const b = checkIsConditionMatchedForMultiSelect({
                    operator: condition?.Operator__c,
                    answerArray: answerArray,
                    answerToMatch: condition?.Value__c,
                });
                booleanAnswerData[condition?.Condition_Order__c] = b;
            } else {
                const b = checkIsConditionMatched({
                    operator: condition?.Operator__c,
                    answer: sourceQuestion?.answer,
                    answerToMatch: condition?.Value__c,
                });
                booleanAnswerData[condition?.Condition_Order__c] = b;
            }
        }

        const b = evaluateCondition(conditionalLogic, booleanAnswerData);

        return b;
    }
    return true;
}
